import { FileText, Search, ChevronRight, Zap } from "lucide-react";

export const LandingNews = () => {
  const emotionsList = ["Abatimento", "Aceitação", "Admiração", "Aflição"];
  const reportSections = [
    "Resumo Emocional",
    "Celebrações da Semana",
    "Padrões Identificados",
    "Sugestões para Próxima Semana",
    "Sugestões de Mensagens",
    "Evolução",
  ];

  return (
    <section className="relative w-full py-16 md:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#D8DEE9]/30 via-white to-[#FFD166]/10 -z-10"></div>

      {/* Animated Blobs */}
      <div
        className="absolute top-20 left-[15%] w-[600px] h-[600px] rounded-full opacity-12 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.4) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "float 22s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-20 right-[10%] w-[500px] h-[500px] rounded-full opacity-15 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.6) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "float 28s ease-in-out infinite reverse",
        }}
      ></div>

      <div className="max-w-6xl mx-auto">
        {/* Badge */}
        <div
          className="flex justify-center mb-8"
          style={{
            animation: "fadeInUp 0.8s ease-out",
          }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFD166]/30 backdrop-blur-sm border border-[#FFD166]/40 text-[#4A4E69] font-semibold text-sm shadow-lg transition-all duration-300 hover:bg-[#FFD166]/40 hover:scale-105">
            <span className="inline-block w-2 h-2 rounded-full bg-[#FFD166] animate-pulse"></span>
            Novidades
          </span>
        </div>

        {/* Title */}
        <div
          className="text-center mb-12"
          style={{
            animation: "fadeInUp 0.8s ease-out 0.2s backwards",
          }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-4">Novas funcionalidades para você</h2>
          <p className="text-base md:text-lg text-[#272D37]/70 max-w-3xl mx-auto">
            Conheça as novas ferramentas que vão enriquecer sua jornada de clareza mental.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Card 1 - Lista de Emoções */}
          <div
            className="group bg-white/60 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-[#D8DEE9]/50 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
            style={{
              animation: "fadeInLeft 0.8s ease-out 0.4s backwards",
              boxShadow: "0 15px 50px rgba(74, 78, 105, 0.1)",
            }}
          >
            <h3 className="text-xl md:text-2xl font-bold text-[#4A4E69] mb-3">Lista de Emoções</h3>
            <p className="text-base text-[#272D37]/80 mb-6 leading-relaxed">
              Saiba o nome exato das emoções que estiver sentindo através da lista de emoções, com mais de 100 emoções
              com descrições detalhadas. Use a busca por texto ou deixe a IA identificar o que você está sentindo.
            </p>

            {/* Mockup - Emotions Modal */}
            <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-[#D8DEE9]/60 overflow-hidden shadow-lg transition-all duration-500 group-hover:shadow-xl">
              {/* Header */}
              <div className="px-4 py-3 border-b border-[#D8DEE9]/40 flex items-center justify-between bg-white/50">
                <span className="font-semibold text-[#4A4E69] text-sm">Lista de Emoções</span>
              </div>

              {/* AI Search Button */}
              <div className="p-3">
                <div className="bg-gradient-to-r from-[#FFD166] to-[#FFD166]/90 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 text-[#4A4E69] font-medium text-sm shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
                  <Zap className="w-4 h-4" />
                  Busca Aprimorada IA
                </div>
              </div>

              {/* Search Input */}
              <div className="px-3 pb-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#D8DEE9]/20 backdrop-blur-sm rounded-xl border border-[#D8DEE9]/30 transition-all duration-300 hover:bg-[#D8DEE9]/30">
                  <Search className="w-4 h-4 text-[#4A4E69]/60" />
                  <span className="text-sm text-[#4A4E69]/60">Buscar emoção...</span>
                </div>
              </div>

              {/* Emotions List */}
              <div className="px-3 pb-3 space-y-1.5">
                {emotionsList.map((emotion, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-[#FFD166]/10 transition-all duration-300 hover:translate-x-1 group/item"
                  >
                    <span className="text-sm text-[#272D37] font-medium">{emotion}</span>
                    <ChevronRight className="w-4 h-4 text-[#4A4E69]/60 transition-transform duration-300 group-hover/item:translate-x-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 2 - Relatório Semanal */}
          <div
            className="group bg-white/60 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-[#D8DEE9]/50 shadow-xl transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
            style={{
              animation: "fadeInRight 0.8s ease-out 0.4s backwards",
              boxShadow: "0 15px 50px rgba(74, 78, 105, 0.1)",
            }}
          >
            <h3 className="text-xl md:text-2xl font-bold text-[#4A4E69] mb-3">Gerar Relatórios</h3>
            <p className="text-base text-[#272D37]/80 mb-6 leading-relaxed">
              Gere relatórios semanalmente com análises inteligentes que destacam progressos, padrões de comportamento
              identificados nas conversas, comparações com as semanas anteriores e sugestões personalizadas para ampliar
              sua jornada de bem-estar mental.
            </p>

            {/* Mockup - Report */}
            <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-[#D8DEE9]/60 overflow-hidden shadow-lg transition-all duration-500 group-hover:shadow-xl">
              {/* Button Mockup */}
              <div className="p-3">
                <div className="bg-gradient-to-r from-[#FFD166] to-[#FFD166]/90 rounded-xl px-4 py-2.5 flex items-center justify-center gap-2 text-[#4A4E69] font-medium text-sm shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02]">
                  <FileText className="w-4 h-4" />
                  Gerar Relatório
                </div>
              </div>

              {/* Report Sections */}
              <div className="px-3 pb-3 space-y-1.5">
                {reportSections.map((section, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-3 py-2.5 bg-[#D8DEE9]/20 backdrop-blur-sm rounded-xl border border-[#D8DEE9]/30 transition-all duration-300 hover:bg-[#FFD166]/10 hover:border-[#FFD166]/30 hover:translate-x-1 group/item"
                  >
                    <span className="text-sm text-[#272D37] font-medium">{section}</span>
                    <ChevronRight className="w-4 h-4 text-[#4A4E69]/60 transition-transform duration-300 group-hover/item:translate-x-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(40px, 40px) scale(1.12); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
};
